import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import ReactJson from 'react18-json-view';
import 'react18-json-view/src/style.css';
import Papa from 'papaparse';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { MRT_Localization_EN } from 'material-react-table/locales/en';
import useApi, { PROGRAMS } from '../hooks/useApi';
import {
  formatActionRequiredReasonSummary,
  formatStatusLabel,
  getActionRequiredReasonData,
  getConsentStatusMeta,
  getDaysRemainingMeta,
  getProductSelectionStatusMeta,
} from '../utils/status';

const ALL_PROGRAMS = 'All Programs';
const PROGRAM_OPTIONS = [ALL_PROGRAMS, ...PROGRAMS];

const safeJsonStringify = (value) => {
  const seen = new WeakSet();
  try {
    return JSON.stringify(
      value,
      (_key, val) => {
        if (typeof val === 'bigint') return val.toString();
        if (typeof val === 'function') return '[function]';
        if (!val || typeof val !== 'object') return val;
        if (seen.has(val)) return '[circular]';
        seen.add(val);
        return val;
      },
      0
    );
  } catch (_err) {
    try {
      return String(value);
    } catch {
      return '';
    }
  }
};

const toCsv = (rows) => Papa.unparse(rows || []);

const downloadText = (filename, text) => {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const ActionRequiredTab = ({ onNavigateToDealer, onOpenDetail }) => {
  const { useActionRequiredWatch, useDiOrderItemsByReferenceId } = useApi();
  const { mutateAsync: fetchDiOrderItemsByReferenceId } = useDiOrderItemsByReferenceId();
  const theme = useTheme();
  const [programFilter, setProgramFilter] = useState(ALL_PROGRAMS);
  const [minRemaining, setMinRemaining] = useState(''); // string input
  const [diByReferenceId, setDiByReferenceId] = useState({});
  const diLoadedRefsRef = useRef(new Set());

  const programName = programFilter === ALL_PROGRAMS ? null : programFilter;
  const minDaysRemaining =
    String(minRemaining).trim() === '' ? null : (Number.isFinite(Number(minRemaining)) ? Number(minRemaining) : null);

  const query = useActionRequiredWatch({
    programName: programName || undefined,
    minDaysRemaining: minDaysRemaining ?? undefined,
  });

  const rows = useMemo(() => (Array.isArray(query.data) ? query.data : []), [query.data]);

  const getRowSelections = useCallback((row) => {
    const enrollment = row?.currentEnrollmentData || row?.currentEnrollment || row;
    if (Array.isArray(enrollment?.productSelections)) return enrollment.productSelections;
    if (Array.isArray(row?.productSelections)) return row.productSelections;
    return [];
  }, []);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      const refs = Array.from(
        new Set(
          (rows || [])
            .flatMap((row) => getRowSelections(row))
            .map((selection) => (selection?.referenceId != null ? String(selection.referenceId).trim() : ''))
            .filter(Boolean)
        )
      );
      if (!refs.length) return;
      const missing = refs.filter((ref) => !diLoadedRefsRef.current.has(ref));
      if (!missing.length) return;
      try {
        const result = await fetchDiOrderItemsByReferenceId(missing);
        const byRef = result?.byReferenceId || {};
        missing.forEach((ref) => diLoadedRefsRef.current.add(ref));
        if (!mounted) return;
        setDiByReferenceId((prev) => ({ ...(prev || {}), ...(byRef || {}) }));
      } catch (error) {
        console.warn('[ActionRequiredTab] Failed to load DI fields', { message: error?.message });
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [rows, fetchDiOrderItemsByReferenceId, getRowSelections]);

  const getDiStatusCounts = useCallback(
    (row) => {
      const selections = getRowSelections(row);
      const counts = selections.reduce((acc, selection) => {
        const ref = selection?.referenceId != null ? String(selection.referenceId).trim() : '';
        const di = ref ? diByReferenceId?.[ref] : null;
        const raw = di?.diStatus ?? selection?.diStatus;
        const key = raw == null ? '' : String(raw).trim();
        if (!key) return acc;
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
      return counts;
    },
    [diByReferenceId, getRowSelections]
  );

  const renderStatusChip = useCallback((meta) => {
    if (!meta?.label) return '—';
    return <Chip size="small" variant="outlined" color={meta.color || 'default'} label={meta.label} />;
  }, []);

  const renderChipList = useCallback((labels) => {
    const list = Array.isArray(labels) ? labels.filter(Boolean) : [];
    if (!list.length) return '—';
    return (
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
        {list.map((label, idx) => (
          <Chip key={`${label}-${idx}`} size="small" variant="outlined" label={label} />
        ))}
      </Stack>
    );
  }, []);

  const resolveReasonValue = useCallback((row, key) => {
    const reason = getActionRequiredReasonData(row);
    if (reason && reason[key] != null && String(reason[key]).trim() !== '') return reason[key];
    const enrollment = row?.currentEnrollmentData || row?.currentEnrollment || {};
    if (enrollment && enrollment[key] != null && String(enrollment[key]).trim() !== '') return enrollment[key];
    return null;
  }, []);

  const resolveEnrollmentDateSort = useCallback((row) => {
    const enrollment = row?.currentEnrollmentData || row?.currentEnrollment || row || {};
    const raw =
      enrollment?.enrollmentDate ||
      enrollment?.enrolledDt ||
      enrollment?.enrollmentDt ||
      enrollment?.enrollmentDateDisplay ||
      null;
    if (!raw) return Number.POSITIVE_INFINITY;
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? Number.POSITIVE_INFINITY : parsed.getTime();
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'bac',
        header: 'BAC',
        size: 90,
        Cell: ({ cell, row }) => (
          <Button
            size="small"
            variant="text"
            onClick={(event) => {
              event.stopPropagation();
              onNavigateToDealer?.(String(cell.getValue() || row.original?.bac || '').trim());
            }}
          >
            {String(cell.getValue() || '')}
          </Button>
        ),
      },
      { accessorKey: 'programName', header: 'Program', size: 140 },
      {
        accessorFn: (row) => {
          const value = Number(row?.daysInStatus);
          return Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
        },
        id: 'daysInStatus',
        header: 'Days In Status',
        size: 120,
      },
      {
        accessorFn: (row) => {
          const value = Number(row?.daysRemaining);
          return Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
        },
        id: 'daysRemaining',
        header: 'Days Remaining',
        size: 130,
        Cell: ({ row }) => {
          const remaining = row.original?.daysRemaining;
          const threshold = row.original?.thresholdDays;
          const meta = getDaysRemainingMeta(remaining, threshold);
          return (
            <Chip
              size="small"
              variant="outlined"
              color={meta.color || 'default'}
              label={meta.label}
            />
          );
        },
      },
      {
        accessorFn: (row) => {
          const value = Number(row?.thresholdDays);
          return Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
        },
        id: 'thresholdDays',
        header: 'Threshold',
        size: 110,
      },
      {
        accessorFn: (row) => resolveEnrollmentDateSort(row),
        id: 'enrollmentDate',
        header: 'Enrollment Date',
        size: 160,
        Cell: ({ row }) => {
          const enrollment = row.original?.currentEnrollmentData || row.original?.currentEnrollment || row.original;
          const value =
            enrollment?.enrollmentDate ||
            enrollment?.enrolledDt ||
            enrollment?.enrollmentDt ||
            enrollment?.enrollmentDateDisplay ||
            '';
          const asString = value == null ? '' : String(value).trim();
          if (!asString) return '';
          const dateOnly = asString.includes('T') ? asString.split('T')[0] : asString.split(' ')[0];
          return dateOnly;
        },
      },
      {
        accessorFn: (row) => {
          if (!row?.firstEnteredAt) return Number.POSITIVE_INFINITY;
          const parsed = new Date(row.firstEnteredAt);
          return Number.isNaN(parsed.getTime()) ? Number.POSITIVE_INFINITY : parsed.getTime();
        },
        id: 'firstEnteredAt',
        header: 'First Entered',
        size: 120,
        Cell: ({ row }) => row.original?.firstEnteredAt || '',
      },
      {
        accessorFn: (row) => {
          if (!row?.lastCheckedDate) return Number.POSITIVE_INFINITY;
          const parsed = new Date(row.lastCheckedDate);
          return Number.isNaN(parsed.getTime()) ? Number.POSITIVE_INFINITY : parsed.getTime();
        },
        id: 'lastCheckedDate',
        header: 'Last Checked',
        size: 120,
        Cell: ({ row }) => row.original?.lastCheckedDate || '',
      },
      {
        accessorKey: 'reasonSummary',
        header: 'Reason',
        size: 520,
        Cell: ({ row }) => formatActionRequiredReasonSummary(row.original),
      },
      {
        id: 'programConsentStatusCd',
        header: 'Program Consent',
        size: 150,
        Cell: ({ row }) => {
          return renderStatusChip(getConsentStatusMeta(resolveReasonValue(row.original, 'programConsentStatusCd')));
        },
      },
      {
        id: 'dataConsentStatusCd',
        header: 'Data Consent',
        size: 140,
        Cell: ({ row }) => {
          return renderStatusChip(getConsentStatusMeta(resolveReasonValue(row.original, 'dataConsentStatusCd')));
        },
      },
      {
        id: 'programSelectionStatusCd',
        header: 'Program Selection',
        size: 170,
        Cell: ({ row }) => {
          return renderStatusChip(getProductSelectionStatusMeta(resolveReasonValue(row.original, 'programSelectionStatusCd')));
        },
      },
      {
        id: 'productSelectionStatusCd',
        header: 'Product Selection',
        size: 170,
        Cell: ({ row }) => {
          return renderStatusChip(getProductSelectionStatusMeta(resolveReasonValue(row.original, 'productSelectionStatusCd')));
        },
      },
      {
        id: 'incompleteSteps',
        header: 'Incomplete Steps',
        size: 220,
        Cell: ({ row }) => {
          const reason = getActionRequiredReasonData(row.original);
          const steps = Array.isArray(reason?.incompleteSteps)
            ? reason.incompleteSteps.map((step) => formatStatusLabel(String(step || '').replace(/([a-z])([A-Z])/g, '$1 $2')))
            : [];
          return renderChipList(steps);
        },
      },
      {
        id: 'selectionStatusCounts',
        header: 'Selection Statuses',
        size: 240,
        Cell: ({ row }) => {
          const reason = getActionRequiredReasonData(row.original);
          const counts = reason?.selectionStatusCounts && typeof reason.selectionStatusCounts === 'object'
            ? Object.entries(reason.selectionStatusCounts)
                .filter(([, value]) => value != null && String(value).trim() !== '')
                .map(([status, count]) => `${formatStatusLabel(status)}: ${count}`)
            : [];
          return renderChipList(counts);
        },
      },
      {
        id: 'diStatus',
        header: '(DI) Status',
        size: 150,
        Cell: ({ row }) => {
          const counts = getDiStatusCounts(row.original);
          const entries = Object.entries(counts)
            .map(([status, count]) => `${formatStatusLabel(status)}: ${count}`)
            .filter(Boolean);
          return renderChipList(entries);
        },
      },
    ],
    [getDiStatusCounts, onNavigateToDealer, renderChipList, renderStatusChip, resolveEnrollmentDateSort, resolveReasonValue]
  );

  const table = useMaterialReactTable({
    columns,
    data: rows,
    localization: MRT_Localization_EN,
    enableColumnOrdering: true,
    enableColumnResizing: true,
    enableStickyHeader: true,
    enableRowSelection: true,
    enablePagination: true,
    enableGlobalFilter: true,
    initialState: {
      density: 'compact',
      pagination: { pageIndex: 0, pageSize: 50 },
      sorting: [{ id: 'daysRemaining', desc: false }],
    },
    muiTableBodyRowProps: ({ row, theme: rowTheme }) => {
      const remaining = Number(row.original?.daysRemaining);
      const threshold = Number(row.original?.thresholdDays);
      const resolvedTheme = rowTheme || theme;
      const base = {
        onClick: (event) => {
          if (event?.defaultPrevented) return;
          const target = event?.target;
          if (target?.closest?.('button, a, input, [role="checkbox"]')) return;
          onOpenDetail?.(row.original);
        },
        sx: { cursor: onOpenDetail ? 'pointer' : 'default' },
      };
      if (!Number.isFinite(remaining) || !Number.isFinite(threshold)) return base;
      if (!resolvedTheme?.palette) return base;
      if (remaining <= 10) {
        return { ...base, sx: { ...base.sx, backgroundColor: alpha(resolvedTheme.palette.error.main, 0.06) } };
      }
      if (remaining <= Math.ceil(threshold / 2)) {
        return { ...base, sx: { ...base.sx, backgroundColor: alpha(resolvedTheme.palette.warning.main, 0.06) } };
      }
      return base;
    },
    renderDetailPanel: ({ row }) => {
      const reason = row.original?.reason;
      const currentEnrollmentData = row.original?.currentEnrollmentData;
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Watch Details
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Reason (structured)
              </Typography>
              <ReactJson src={reason || {}} collapsed={1} />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Current Enrollment Data
              </Typography>
              <ReactJson src={currentEnrollmentData || {}} collapsed={1} />
            </Box>
          </Stack>
        </Box>
      );
    },
    state: {
      isLoading: query.isLoading,
      showAlertBanner: Boolean(query.error),
      showProgressBars: query.isFetching,
    },
    muiToolbarAlertBannerProps: query.error
      ? { color: 'error', children: query.error?.message || 'Failed to load Action Required watchdog list.' }
      : undefined,
  });

  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (query.error) {
    return <Alert severity="error">{query.error?.message || 'Failed to load Action Required watchdog list.'}</Alert>;
  }

  const selectedRows = table.getSelectedRowModel().rows.map((r) => r.original);

  const handleCopy = async (format) => {
    const payload = format === 'json' ? safeJsonStringify(selectedRows) : toCsv(selectedRows);
    await navigator.clipboard.writeText(payload);
  };

  const handleExport = (format) => {
    const payload = format === 'json' ? safeJsonStringify(selectedRows.length ? selectedRows : rows) : toCsv(selectedRows.length ? selectedRows : rows);
    const filename = `action-required-watchdog.${format === 'json' ? 'json' : 'csv'}`;
    downloadText(filename, payload);
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ md: 'flex-end' }}>
        <FormControl sx={{ minWidth: 240 }} size="small">
          <InputLabel>Program</InputLabel>
          <Select value={programFilter} label="Program" onChange={(e) => setProgramFilter(e.target.value)}>
            {PROGRAM_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 220 }} size="small">
          <InputLabel>Min days remaining</InputLabel>
          <Select value={minRemaining} label="Min days remaining" onChange={(e) => setMinRemaining(e.target.value)}>
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="0">0+</MenuItem>
            <MenuItem value="5">5+</MenuItem>
            <MenuItem value="10">10+</MenuItem>
            <MenuItem value="15">15+</MenuItem>
            <MenuItem value="30">30+</MenuItem>
          </Select>
        </FormControl>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Tooltip title="Refresh">
            <Button size="small" variant="outlined" onClick={() => query.refetch()}>
              Refresh
            </Button>
          </Tooltip>
          <Tooltip title="Copy selected rows as CSV (or all filtered if none selected)">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                disabled={rows.length === 0}
                onClick={() => void handleCopy('csv')}
              >
                Copy CSV
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Copy selected rows as JSON (or all filtered if none selected)">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                disabled={rows.length === 0}
                onClick={() => void handleCopy('json')}
              >
                Copy JSON
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Export selected rows (or all rows if none selected)">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                disabled={rows.length === 0}
                onClick={() => handleExport('csv')}
              >
                Export CSV
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Export selected rows (or all rows if none selected)">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                disabled={rows.length === 0}
                onClick={() => handleExport('json')}
              >
                Export JSON
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <MaterialReactTable table={table} />

      <Box sx={{ mt: 1, color: 'text.secondary', fontSize: 12 }}>
        Showing {rows.length} dealer-program row(s). Selected {selectedRows.length}.
      </Box>
    </Box>
  );
};

export default ActionRequiredTab;
